import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import { FaPlus, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import SuccessModal from "../components/modals/SuccessModal";
import { useNotificationModal } from "../hooks/useNotificationModal";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function StockAllocation() {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();
  const { gtn_id } = useParams();

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  useEffect(() => {}, [userData]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isViewMode] = useState(!!gtn_id);
  const [viewLoading, setViewLoading] = useState(!!gtn_id);
  const [gtnStatus, setGtnStatus] = useState(null);
  const [gtnNumber, setGtnNumber] = useState(null);

  const [sites, setSites] = useState([]);
  const [availableStock, setAvailableStock] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockInputQty, setStockInputQty] = useState({});
  const [stockSearchFilters, setStockSearchFilters] = useState({
    sku: "",
    style_name: "",
    batch_number: "",
  });
  const [selectedStockItems, setSelectedStockItems] = useState(new Set());
  const [bulkQtyInput, setBulkQtyInput] = useState("");
  const [gtnSearchFilters, setGtnSearchFilters] = useState({
    sku: "",
    product_name: "",
    batch_number: "",
  });

  const [formData, setFormData] = useState({
    from_site_id: "",
    to_site_id: "",
    transfer_type: "warehouse_to_plant",
    transfer_date: new Date().toISOString().split("T")[0],
    expected_delivery_date: "",
    transport_mode: "",
    vehicle_number: "",
    driver_name: "",
    driver_contact: "",
    remarks: "",
  });

  const [gtnItems, setGtnItems] = useState([]);

  // Fetch all sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        // Fetch warehouse sites
        const warehouseResponse = await axios.get(
          `${BASE_URL}/api/admin/sites/get-all-sites-based-on-type`,
          {
            params: {
              company_code: userData.company_code,
              type: "warehouse",
            },
          }
        );

        // Fetch plant sites
        const plantResponse = await axios.get(
          `${BASE_URL}/api/admin/sites/get-all-sites-based-on-type`,
          {
            params: {
              company_code: userData.company_code,
              type: "plant",
            },
          }
        );

        if (warehouseResponse.data.success && plantResponse.data.success) {
          // Combine both warehouse and plant sites
          const allSites = [
            ...(warehouseResponse.data.items || []),
            ...(plantResponse.data.items || []),
          ];
          allSites.forEach((site, idx) => {});
          setSites(allSites);

          // Set from_site_id to the main warehouse if not in view mode
          if (!gtn_id) {
            const mainWarehouse = allSites.find(
              (site) => site.site_type?.toLowerCase() === "warehouse"
            );
            if (mainWarehouse) {
              setFormData((prev) => ({
                ...prev,
                from_site_id: mainWarehouse.site_id,
              }));
            } else {
              console.warn("[SITES FETCH] No warehouse site found in the list");
            }
          }
        } else {
          console.warn(
            "[SITES FETCH] One or both API calls returned success: false"
          );
          if (!warehouseResponse.data.success) {
            console.error(
              "[SITES FETCH] Warehouse fetch error:",
              warehouseResponse.data.message
            );
          }
          if (!plantResponse.data.success) {
            console.error(
              "[SITES FETCH] Plant fetch error:",
              plantResponse.data.message
            );
          }
          setError("Failed to fetch sites list");
        }
      } catch (err) {
        console.error("[SITES FETCH] Error fetching sites:", err);
        setError("Failed to fetch sites");
      }
    };

    if (userData?.company_code) {
      fetchSites();
    }
  }, [userData, gtn_id]);

  // Fetch GTN details if viewing existing GTN
  useEffect(() => {
    const fetchGTNDetails = async () => {
      if (!gtn_id || !userData?.company_code) {
        setViewLoading(false);
        return;
      }

      try {
        setViewLoading(true);
        const response = await axios.get(
          `${BASE_URL}/api/admin/stock-allocation/get-gtn/${gtn_id}`,
          {
            params: { company_code: userData.company_code },
          }
        );

        if (response.data.success) {
          const gtn = response.data.gtn;

          // Set GTN status and number
          setGtnStatus(gtn.status);
          setGtnNumber(gtn.gtn_number);

          // Load GTN header details
          setFormData({
            from_site_id: gtn.from_site_id || "",
            to_site_id: gtn.to_site_id || "",
            transfer_type: gtn.transfer_type || "plant_to_plant",
            transfer_date: gtn.transfer_date?.split("T")[0] || "",
            expected_delivery_date:
              gtn.expected_delivery_date?.split("T")[0] || "",
            transport_mode: gtn.transport_mode || "",
            vehicle_number: gtn.vehicle_number || "",
            driver_name: gtn.driver_name || "",
            driver_contact: gtn.driver_contact || "",
            remarks: gtn.remarks || "",
          });

          // Load GTN items
          if (gtn.items && Array.isArray(gtn.items)) {
            setGtnItems(
              gtn.items.map((item) => ({
                sku: item.sku,
                product_name: item.product_name,
                batch_number: item.batch_number,
                available_quantity: item.available_quantity || 0,
                quantity_transferred: item.quantity_transferred,
                uom: item.uom || "",
                expiry_date: item.expiry_date || "",
                manufacturing_date: item.manufacturing_date || "",
                condition_on_transfer: item.condition_on_transfer || "good",
                item_remarks: item.item_remarks || "",
              }))
            );
          }
        } else {
          setError(response.data.message || "Failed to load GTN details");
        }
      } catch (err) {
        console.error("Error fetching GTN details:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Error loading GTN details",
          "Error"
        );
      } finally {
        setViewLoading(false);
      }
    };

    fetchGTNDetails();
  }, [gtn_id, userData]);

  // Fetch available stock when from_site is selected
  useEffect(() => {
    const fetchStock = async () => {
      if (!formData.from_site_id) {
        setAvailableStock([]);
        return;
      }

      setLoadingStock(true);
      try {
        // Find the selected site to check its type
        const selectedSite = sites.find(
          (site) => site.site_id === Number(formData.from_site_id)
        );

        // Check if the selected site is a warehouse
        if (selectedSite?.site_type?.toLowerCase() === "warehouse") {
          const response = await axios.get(
            `${BASE_URL}/api/admin/stock/main-stock`,
            {
              params: {
                company_code: userData?.company_code,
                page: 1,
                limit: 1000,
              },
            }
          );
          if (response.data.success) {
            const stockData = response.data.data || [];
            setAvailableStock(stockData);
            if (!stockData || stockData.length === 0) {
              console.warn("[STOCK FETCH] No stock items returned from API");
            }
          } else {
            console.warn(
              "[STOCK FETCH] Stock API returned success: false",
              response.data
            );
            setError(
              "Failed to fetch stock: " +
                (response.data.message || "Unknown error")
            );
          }
        } else {
          console.warn(
            "[STOCK FETCH] Selected site is not a warehouse:",
            selectedSite?.site_type
          );
          setAvailableStock([]);
        }
      } catch (err) {
        console.error("[STOCK FETCH] Error fetching stock:", err);
        setError(
          "Error fetching available stock: " + (err.message || "Unknown error")
        );
      } finally {
        setLoadingStock(false);
      }
    };

    fetchStock();
  }, [formData.from_site_id, userData, sites]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStockSearchChange = (e) => {
    const { name, value } = e.target;
    setStockSearchFilters((prev) => ({
      ...prev,
      [name]: value.toLowerCase(),
    }));
  };

  const getFilteredStock = () => {
    return availableStock.filter((stock) => {
      return (
        (stockSearchFilters.sku === "" ||
          stock.sku.toLowerCase().includes(stockSearchFilters.sku)) &&
        (stockSearchFilters.style_name === "" ||
          (stock.style_name &&
            stock.style_name
              .toLowerCase()
              .includes(stockSearchFilters.style_name))) &&
        (stockSearchFilters.batch_number === "" ||
          stock.batch_number
            .toLowerCase()
            .includes(stockSearchFilters.batch_number))
      );
    });
  };

  const handleStockCheckboxChange = (stockKey) => {
    setSelectedStockItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stockKey)) {
        newSet.delete(stockKey);
      } else {
        newSet.add(stockKey);
      }
      return newSet;
    });
  };

  const handleSelectAllCheckboxes = () => {
    if (selectedStockItems.size === getFilteredStock().length) {
      setSelectedStockItems(new Set());
    } else {
      const allKeys = new Set(
        getFilteredStock().map((stock) => `${stock.sku}-${stock.batch_number}`)
      );
      setSelectedStockItems(allKeys);
    }
  };

  const addBulkQtyToSelected = () => {
    if (selectedStockItems.size === 0) {
      setError("Please select at least one item", "Validation Error");
      return;
    }

    const qty = parseFloat(bulkQtyInput);
    if (!qty || qty <= 0) {
      setError("Please enter a valid quantity greater than 0");
      setTimeout(() => setError(null), 3000);
      return;
    }

    const filteredStock = getFilteredStock();
    const selectedSite = sites.find(
      (site) => site.site_id === Number(formData.from_site_id)
    );
    const isWarehouse = selectedSite?.site_type?.toLowerCase() === "warehouse";

    const itemsToAdd = [];
    const failedItems = [];

    selectedStockItems.forEach((stockKey) => {
      const stock = filteredStock.find(
        (s) => `${s.sku}-${s.batch_number}` === stockKey
      );

      if (stock) {
        const availableQty = isWarehouse
          ? stock.main_stock_qty
          : stock.quantity || stock.main_stock_qty;

        // Check if quantity exceeds available stock
        if (qty > availableQty) {
          failedItems.push(`${stock.sku} (available: ${availableQty})`);
          return;
        }

        // Check if item already added
        const exists = gtnItems.find(
          (item) =>
            item.sku === stock.sku && 
            item.batch_number === stock.batch_number &&
            item.lot_no === stock.lot_no
        );

        if (exists) {
          failedItems.push(`${stock.sku} (already added)`);
          return;
        }

        itemsToAdd.push({
          sku: stock.sku,
          product_name: stock.product_name || stock.style_name,
          batch_number: stock.batch_number,
          lot_no: stock.lot_no || '',
          available_quantity: availableQty,
          quantity_transferred: qty,
          uom: stock.uom,
          expiry_date: stock.expiry_date,
          manufacturing_date: stock.manufacturing_date,
          condition_on_transfer: "good",
          item_remarks: "",
        });
      }
    });

    if (itemsToAdd.length > 0) {
      setGtnItems((prev) => [...prev, ...itemsToAdd]);
      setBulkQtyInput("");
      setSelectedStockItems(new Set());

      let successMsg = `${itemsToAdd.length} item(s) added to transfer list`;
      if (failedItems.length > 0) {
        successMsg += ` (${failedItems.length} failed)`;
      }
      setSuccess(successMsg);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(`Failed to add items: ${failedItems.join(", ")}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  const addItemToGTN = (stockItem) => {
    // Get the quantity from input
    const qty =
      parseFloat(stockInputQty[`${stockItem.sku}-${stockItem.batch_number}`]) ||
      0;

    if (qty <= 0) {
      setError("Please enter a valid quantity greater than 0");
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Check available quantity based on source type
    const selectedSite = sites.find(
      (site) => site.site_id === Number(formData.from_site_id)
    );
    const isWarehouse = selectedSite?.site_type?.toLowerCase() === "warehouse";
    const availableQty = isWarehouse
      ? stockItem.main_stock_qty
      : stockItem.quantity || stockItem.main_stock_qty;

    if (qty > availableQty) {
      setError(`Quantity exceeds available stock (${availableQty})`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Check if item already added
    const exists = gtnItems.find(
      (item) =>
        item.sku === stockItem.sku &&
        item.batch_number === stockItem.batch_number &&
        item.lot_no === stockItem.lot_no
    );

    if (exists) {
      setError("This item is already added to the GTN");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setGtnItems((prev) => [
      ...prev,
      {
        sku: stockItem.sku,
        product_name: stockItem.product_name || stockItem.style_name,
        batch_number: stockItem.batch_number,
        lot_no: stockItem.lot_no || '',
        available_quantity: availableQty,
        quantity_transferred: qty,
        uom: stockItem.uom,
        expiry_date: stockItem.expiry_date,
        manufacturing_date: stockItem.manufacturing_date,
        condition_on_transfer: "good",
        item_remarks: "",
      },
    ]);

    // Clear the input quantity after adding
    setStockInputQty((prev) => {
      const newQty = { ...prev };
      delete newQty[`${stockItem.sku}-${stockItem.batch_number}`];
      return newQty;
    });
  };

  const removeItemFromGTN = (index) => {
    setGtnItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItemQuantity = (index, quantity) => {
    setGtnItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, quantity_transferred: parseFloat(quantity) || 0 }
          : item
      )
    );
  };

  const updateItemRemarks = (index, remarks) => {
    setGtnItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, item_remarks: remarks } : item
      )
    );
  };

  const handleGtnSearchChange = (e) => {
    const { name, value } = e.target;
    setGtnSearchFilters((prev) => ({
      ...prev,
      [name]: value.toLowerCase(),
    }));
  };

  const getFilteredGtnItems = () => {
    return gtnItems.filter((item) => {
      return (
        (gtnSearchFilters.sku === "" ||
          item.sku.toLowerCase().includes(gtnSearchFilters.sku)) &&
        (gtnSearchFilters.product_name === "" ||
          item.product_name
            .toLowerCase()
            .includes(gtnSearchFilters.product_name)) &&
        (gtnSearchFilters.batch_number === "" ||
          item.batch_number
            .toLowerCase()
            .includes(gtnSearchFilters.batch_number))
      );
    });
  };

  const validateForm = () => {
    if (!formData.from_site_id || !formData.to_site_id) {
      setError("Please select both source and destination sites");
      return false;
    }

    if (formData.from_site_id === formData.to_site_id) {
      setError("Source and destination sites cannot be the same");
      return false;
    }

    if (gtnItems.length === 0) {
      setError("Please add at least one item to the GTN");
      return false;
    }

    // Validate quantities
    for (let item of gtnItems) {
      if (item.quantity_transferred <= 0) {
        setError(`Please enter valid quantity for ${item.product_name}`);
        return false;
      }
      if (item.quantity_transferred > item.available_quantity) {
        setError(
          `Quantity for ${item.product_name} exceeds available stock (${item.available_quantity})`
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      setTimeout(() => setError(null), 5000);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/admin/stock-allocation/create-gtn`,
        {
          company_code: userData.company_code,
          from_site_id: formData.from_site_id,
          to_site_id: formData.to_site_id,
          transfer_type: formData.transfer_type,
          transfer_date: formData.transfer_date,
          expected_delivery_date: formData.expected_delivery_date || null,
          created_by: userData.id,
          transport_mode: formData.transport_mode,
          vehicle_number: formData.vehicle_number,
          driver_name: formData.driver_name,
          driver_contact: formData.driver_contact,
          remarks: formData.remarks,
          items: gtnItems,
        }
      );

      if (response.data.success) {
        showSuccess(
          `GTN created successfully with PENDING status! GTN Number: ${response.data.gtnNumber}`,
          "Success"
        );
        setTimeout(() => {
          navigate("/warehouse/allocate-stock/history");
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to create GTN");
      }
    } catch (err) {
      console.error("Error creating GTN:", err);
      showError(
        err.response?.data?.message ||
          err.message ||
          "Error creating GTN. Please try again.",
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApproveGTN = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const response = await axios.put(
        `${BASE_URL}/api/admin/stock-allocation/update-status/${gtn_id}`,
        {
          company_code: userData.company_code,
          status: "approved",
          user_id: userData.id,
        }
      );

      if (response.data.success) {
        showSuccess(`GTN #${gtnNumber} approved successfully!`, "Success");
        setTimeout(() => {
          navigate("/warehouse/allocate-stock/history");
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to approve GTN");
      }
    } catch (err) {
      console.error("Error approving GTN:", err);
      showError(
        err.response?.data?.message ||
          err.message ||
          "Error approving GTN. Please try again.",
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRejectGTN = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const response = await axios.put(
        `${BASE_URL}/api/admin/stock-allocation/update-status/${gtn_id}`,
        {
          company_code: userData.company_code,
          status: "rejected",
          user_id: userData.id,
        }
      );

      if (response.data.success) {
        showSuccess(`GTN #${gtnNumber} rejected successfully!`, "Success");
        setTimeout(() => {
          navigate("/warehouse/allocate-stock/history");
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to reject GTN");
      }
    } catch (err) {
      console.error("Error rejecting GTN:", err);
      showError(
        err.response?.data?.message ||
          err.message ||
          "Error rejecting GTN. Please try again.",
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
        return "info";
      case "received":
        return "success";
      case "completed":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <div className="admin-container mt-4">
      {viewLoading ? (
        <div className="text-center p-5">
          <Spinner animation="border" className="me-2" />
          <p>Loading GTN details...</p>
        </div>
      ) : (
        <>
          <div className="style-header">
            <div>
              <h1>
                {isViewMode
                  ? `Goods Transfer Note (GTN #${gtn_id})`
                  : "Create Goods Transfer Note (GTN)"}
              </h1>
              {isViewMode && gtnStatus && (
                <div className="mt-2">
                  <span
                    style={{
                      color:
                        gtnStatus?.toLowerCase() === "pending"
                          ? "#ff9800"
                          : gtnStatus?.toLowerCase() === "approved"
                          ? "#17a2b8"
                          : gtnStatus?.toLowerCase() === "received" ||
                            gtnStatus?.toLowerCase() === "completed"
                          ? "#28a745"
                          : gtnStatus?.toLowerCase() === "rejected"
                          ? "#dc3545"
                          : "#6c757d",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {gtnStatus?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            <Button
              variant="secondary"
              onClick={() => navigate("/warehouse/allocate-stock/history")}
            >
              Back
            </Button>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          <Form onSubmit={isViewMode ? handleApproveGTN : handleSubmit}>
            {/* GTN Header Information */}
            <Card className="mb-4">
              <Card.Header>
                <h4>Transfer Details</h4>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>To Site (Destination)*</Form.Label>
                      <Form.Select
                        name="to_site_id"
                        value={formData.to_site_id}
                        onChange={handleChange}
                        required
                        disabled={isViewMode}
                      >
                        <option value="">Select destination site</option>
                        {sites
                          .filter(
                            (site) =>
                              site.site_type?.toLowerCase() === "plant" ||
                              site.site_type?.toLowerCase() === "branch" ||
                              site.site_type?.toLowerCase() === "location"
                          )
                          .map((site) => (
                            <option key={site.site_id} value={site.site_id}>
                              {site.site_code} - {site.site_name} (
                              {site.site_type})
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Transfer Date*</Form.Label>
                      <Form.Control
                        type="date"
                        name="transfer_date"
                        value={formData.transfer_date}
                        onChange={handleChange}
                        required
                        disabled={isViewMode}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Expected Delivery Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="expected_delivery_date"
                        value={formData.expected_delivery_date}
                        onChange={handleChange}
                        disabled={isViewMode}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Transport Mode</Form.Label>
                      <Form.Control
                        type="text"
                        name="transport_mode"
                        value={formData.transport_mode}
                        onChange={handleChange}
                        placeholder="e.g., Truck, Van, Container"
                        disabled={isViewMode}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Vehicle Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="vehicle_number"
                        value={formData.vehicle_number}
                        onChange={handleChange}
                        placeholder="Enter vehicle number"
                        disabled={isViewMode}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Driver Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="driver_name"
                        value={formData.driver_name}
                        onChange={handleChange}
                        placeholder="Enter driver name"
                        disabled={isViewMode}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Driver Contact</Form.Label>
                      <Form.Control
                        type="text"
                        name="driver_contact"
                        value={formData.driver_contact}
                        onChange={handleChange}
                        placeholder="Enter driver contact number"
                        disabled={isViewMode}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Remarks</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Enter any additional remarks"
                        disabled={isViewMode}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Available Stock */}
            {formData.from_site_id && !isViewMode && (
              <Card className="mb-4">
                <Card.Header>
                  <h4>
                    Available Stock at{" "}
                    {sites
                      .find(
                        (site) => site.site_id === Number(formData.from_site_id)
                      )
                      ?.site_type?.toLowerCase() === "warehouse"
                      ? "(Warehouse)"
                      : "Site"}
                  </h4>
                </Card.Header>
                <Card.Body>
                  {loadingStock ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" />
                      <p>Loading stock...</p>
                    </div>
                  ) : availableStock.length === 0 ? (
                    <Alert variant="info">
                      No stock available at{" "}
                      {sites
                        .find(
                          (site) =>
                            site.site_id === Number(formData.from_site_id)
                        )
                        ?.site_type?.toLowerCase() === "warehouse"
                        ? "warehouse"
                        : "site"}
                    </Alert>
                  ) : (
                    <div>
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="fw-bold">
                              Bulk Add Quantity
                              {selectedStockItems.size > 0 && (
                                <span className="ms-2 badge bg-info">
                                  {selectedStockItems.size} selected
                                </span>
                              )}
                            </Form.Label>
                            <div className="d-flex gap-2">
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={bulkQtyInput}
                                onChange={(e) =>
                                  setBulkQtyInput(e.target.value)
                                }
                                placeholder="Enter quantity for selected items"
                              />
                              <Button
                                variant="primary"
                                onClick={addBulkQtyToSelected}
                                disabled={selectedStockItems.size === 0}
                                title="Apply quantity to selected items"
                              >
                                Apply
                              </Button>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                      <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                        <Table striped bordered hover size="sm">
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              backgroundColor: "#fff",
                              zIndex: 10,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          >
                            <tr>
                              <th style={{ width: "40px" }}>
                                <Form.Check
                                  type="checkbox"
                                  checked={
                                    selectedStockItems.size > 0 &&
                                    selectedStockItems.size ===
                                      getFilteredStock().length
                                  }
                                  onChange={handleSelectAllCheckboxes}
                                  title="Select all"
                                />
                              </th>
                              <th>SKU</th>
                              <th>Product Name</th>
                              <th>Batch Number</th>
                              <th>Available Qty</th>
                              <th>Transfer Qty*</th>
                              <th>Unit Price</th>
                              <th>Sale Price</th>
                              <th>Action</th>
                            </tr>
                            <tr>
                              <th></th>
                              <th>
                                <Form.Control
                                  type="text"
                                  name="sku"
                                  placeholder="Search SKU"
                                  value={stockSearchFilters.sku}
                                  onChange={handleStockSearchChange}
                                  size="sm"
                                  style={{ fontSize: "12px" }}
                                />
                              </th>
                              <th>
                                <Form.Control
                                  type="text"
                                  name="style_name"
                                  placeholder="Search Product"
                                  value={stockSearchFilters.style_name}
                                  onChange={handleStockSearchChange}
                                  size="sm"
                                  style={{ fontSize: "12px" }}
                                />
                              </th>
                              <th>
                                <Form.Control
                                  type="text"
                                  name="batch_number"
                                  placeholder="Search Batch"
                                  value={stockSearchFilters.batch_number}
                                  onChange={handleStockSearchChange}
                                  size="sm"
                                  style={{ fontSize: "12px" }}
                                />
                              </th>
                              <th></th>
                              <th></th>
                              <th></th>
                              <th></th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredStock().map((stock, index) => {
                              const selectedSite = sites.find(
                                (site) =>
                                  site.site_id === Number(formData.from_site_id)
                              );
                              const isWarehouse =
                                selectedSite?.site_type?.toLowerCase() ===
                                "warehouse";
                              const availableQty = isWarehouse
                                ? stock.main_stock_qty
                                : stock.quantity || stock.main_stock_qty;

                              return (
                                <tr key={index}>
                                  <td style={{ width: "40px" }}>
                                    <Form.Check
                                      type="checkbox"
                                      checked={selectedStockItems.has(
                                        `${stock.sku}-${stock.batch_number}`
                                      )}
                                      onChange={() =>
                                        handleStockCheckboxChange(
                                          `${stock.sku}-${stock.batch_number}`
                                        )
                                      }
                                    />
                                  </td>
                                  <td>{stock.sku}</td>
                                  <td>{stock.style_name}</td>
                                  <td>{stock.batch_number}</td>
                                  <td>{availableQty}</td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max={availableQty}
                                      value={
                                        stockInputQty[
                                          `${stock.sku}-${stock.batch_number}`
                                        ] || ""
                                      }
                                      onChange={(e) =>
                                        setStockInputQty((prev) => ({
                                          ...prev,
                                          [`${stock.sku}-${stock.batch_number}`]:
                                            e.target.value,
                                        }))
                                      }
                                      placeholder="Enter qty"
                                      style={{
                                        width: "120px",
                                        height: "25px",
                                        fontSize: "13px",
                                      }}
                                    />
                                  </td>
                                  <td>{stock.unit_price}</td>
                                  <td>{stock.sale_price}</td>
                                  <td>
                                    <FaPlus
                                      variant="success"
                                      onClick={() => addItemToGTN(stock)}
                                      title="Add"
                                      className="add-icon"
                                      style={{ cursor: "pointer" }}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* GTN Items */}
            <Card className="mb-4">
              <Card.Header>
                <h4>
                  {isViewMode ? "Items Transferred" : "Items to Transfer"} (
                  {gtnItems.length})
                </h4>
              </Card.Header>
              <Card.Body>
                {gtnItems.length === 0 ? (
                  <Alert variant="warning">
                    No items added. Please select items from available stock
                    above.
                  </Alert>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>SKU</th>
                        <th>Product Name</th>
                        <th>Batch Number</th>
                        {!isViewMode && <th>Available Stock</th>}
                        <th>Transfer Qty*</th>
                        <th>Remarks</th>
                        {!isViewMode && <th>Action</th>}
                      </tr>
                      <tr>
                        <th></th>
                        <th>
                          <Form.Control
                            type="text"
                            name="sku"
                            placeholder="Search SKU"
                            value={gtnSearchFilters.sku}
                            onChange={handleGtnSearchChange}
                            size="sm"
                            style={{ fontSize: "12px" }}
                          />
                        </th>
                        <th>
                          <Form.Control
                            type="text"
                            name="product_name"
                            placeholder="Search Product"
                            value={gtnSearchFilters.product_name}
                            onChange={handleGtnSearchChange}
                            size="sm"
                            style={{ fontSize: "12px" }}
                          />
                        </th>
                        <th>
                          <Form.Control
                            type="text"
                            name="batch_number"
                            placeholder="Search Batch"
                            value={gtnSearchFilters.batch_number}
                            onChange={handleGtnSearchChange}
                            size="sm"
                            style={{ fontSize: "12px" }}
                          />
                        </th>
                        {!isViewMode && <th></th>}
                        <th></th>
                        <th></th>
                        {!isViewMode && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredGtnItems().map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.sku}</td>
                          <td>{item.product_name}</td>
                          <td>{item.batch_number}</td>
                          {!isViewMode && <td>{item.available_quantity}</td>}
                          <td>
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0"
                              max={item.available_quantity}
                              value={item.quantity_transferred}
                              onChange={(e) =>
                                updateItemQuantity(index, e.target.value)
                              }
                              required
                              style={{
                                width: "120px",
                                height: "25px",
                                fontSize: "13px",
                              }}
                              disabled={isViewMode}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={item.item_remarks}
                              onChange={(e) =>
                                updateItemRemarks(index, e.target.value)
                              }
                              placeholder="Optional"
                              style={{
                                width: "120px",
                                height: "25px",
                                fontSize: "13px",
                              }}
                              disabled={isViewMode}
                            />
                          </td>
                          {!isViewMode && (
                            <td>
                              <FaTrash
                                variant="danger"
                                onClick={() => removeItemFromGTN(index)}
                                title="Remove"
                                className="remove-icon"
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* Submit Button */}
            <div className="text-center mb-4">
              {isViewMode ? (
                gtnStatus === "pending" &&
                userData?.role === "Company_Admin" ? (
                  <div className="d-flex gap-2 justify-content-center">
                    <Button
                      type="submit"
                      variant="success"
                      size="lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Approving GTN...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Approve GTN
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="lg"
                      disabled={loading}
                      onClick={handleRejectGTN}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Rejecting GTN...
                        </>
                      ) : (
                        <>
                          <FaTimes className="me-2" />
                          Reject GTN
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    {" "}
                    <strong>{gtnStatus?.toUpperCase()}</strong>
                  </Alert>
                )
              ) : (
                <Button
                  type="submit"
                  variant="success"
                  size="lg"
                  disabled={loading || gtnItems.length === 0}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating GTN...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Create GTN
                    </>
                  )}
                </Button>
              )}
            </div>
          </Form>
        </>
      )}

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
        backdrop={true}
        keyboard={true}
      />
    </div>
  );
}
